// Listing-mode switcher for Due Date Control — the MAJOR-module layout shape.
//
//   <base>/dashboard  ┐
//   <base>/list       ┼ mode routes, wrapped by this layout's <Outlet/>
//   <base>/templates  ┘
//
// Mirrored from src/ata/ATALayout.tsx. The layout owns the page header (the
// breadcrumb); the modes render no ListingHeader of their own.
import React, { useEffect } from "react";
import { Box, Button, Typography } from "@mui/material";
import { usePopupState } from "material-ui-popup-state/hooks";
import { Outlet, useLocation } from "react-router-dom";

import {
  BreadcrumbChevronIcon,
  BreadcrumbDropdownMenu,
  BreadcrumbHoverRoot,
  BreadcrumbMenuLinkItem,
  describeMode,
} from "../components/BreadcrumbDropdownMenu";
import { ListingRouteBaseProvider } from "../components/datagrid/ListingRouteBase";
import { BreadcrumbHeaderListing, Title } from "../components/Header";
import { LinkRouter } from "../components/Links";
import {
  breadcrumbCurrentLabelSx,
  breadcrumbCurrentTriggerSx,
  breadcrumbTitleSx,
} from "../components/dropdownMenuStyles";

import { MODE_DESCRIPTIONS, MODE_LABELS, MODE_STORAGE_KEY, splitModePath } from "./modes";

interface DueDateLayoutProps {
  /** Module title from the route / nav; roots the breadcrumb. */
  title?: string;
}

const DueDateLayout: React.FC<DueDateLayoutProps> = ({ title }) => {
  const location = useLocation();
  const popupState = usePopupState({
    variant: "popover",
    popupId: "dueDateBreadcrumbMenu",
  });

  const { basePath, currentMode, onMode } = splitModePath(location.pathname);

  useEffect(() => {
    if (!onMode) return;
    try {
      window.localStorage.setItem(MODE_STORAGE_KEY, currentMode);
    } catch {
      // Storage unavailable
    }
  }, [onMode, currentMode]);

  return (
    <Box height="100%">
      <BreadcrumbHeaderListing aria-label="breadcrumb">
        <Title sx={breadcrumbTitleSx}>
          <LinkRouter to={basePath || "/"}>
            {title || "Due Date Control"} /
          </LinkRouter>
          <BreadcrumbHoverRoot popupState={popupState}>
            <Button endIcon={<BreadcrumbChevronIcon />} sx={breadcrumbCurrentTriggerSx}>
              <Typography variant="h2" component="span" sx={breadcrumbCurrentLabelSx}>
                {MODE_LABELS[currentMode] ?? MODE_LABELS.dashboard}
              </Typography>
            </Button>
            <BreadcrumbDropdownMenu popupState={popupState}>
              {Object.entries(MODE_LABELS).map(([segment, label]) => (
                <BreadcrumbMenuLinkItem
                  key={segment}
                  to={`${basePath}/${segment}`}
                  label={label}
                  description={describeMode(MODE_DESCRIPTIONS, segment)}
                  selected={currentMode === segment}
                  onClick={popupState.close}
                />
              ))}
            </BreadcrumbDropdownMenu>
          </BreadcrumbHoverRoot>
        </Title>
      </BreadcrumbHeaderListing>
      {/* The record routes (`create`, `:id`) are siblings of the modes, so the
          grid's row click and the toolbar's (+) resolve against this base
          (ListingRouteBase.tsx, BP-41). */}
      <ListingRouteBaseProvider value={{ base: basePath }}>
        <Outlet />
      </ListingRouteBaseProvider>
    </Box>
  );
};

export default DueDateLayout;
